import { useState, useEffect, useRef, createContext, useContext } from "react";
import "@/App.css";
import { HashRouter, Routes, Route } from "react-router-dom";
import { Music, Instagram, Youtube, Menu, X, Play, Headphones, CalendarDays, MapPin, Users, ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ===== TRANSLATIONS =====
const translations = {
  sq: {
    seo: {
      title: "DJ Smily | Producent Muzikor & DJ Shqiptar - Dasma, Evente & Festivale",
      description: "DJ Smily - Producent muzikor dhe DJ shqiptar. Autor i hiteve 'Aman', 'Vera' dhe 'Kallma cigaren'. Rezervo per dasma, evente private, klube nate dhe festivale.",
      keywords: "DJ Smily, DJ shqiptar, muzike shqiptare, remix shqip, DJ per dasma, Aman, Vera, Kallma cigaren, producent muzikor Kosove, DJ Tirane, DJ Prishtine",
    },
    nav: { home: "Kryefaqja", about: "Rreth Meje", music: "Muzika", videos: "Video", gallery: "Galeria", booking: "Rezervo", bookNow: "REZERVO TANI" },
    hero: {
      overline: "Producent Muzikor & DJ Shqiptar",
      subtitle: "Muzika qe te ben te ndihesh gjalle. Nga hitet popullore deri te remikset me te mira shqiptare.",
      cta: "REZERVO TANI",
      listen: "DEGJO MUZIKEN",
    },
    about: {
      overline: "Rreth Meje",
      title: "Pasioni per",
      titleHighlight: "Muziken",
      bio1: "DJ Smily eshte nje nga DJ-te me te dashur te skenes muzikore shqiptare. Me nje stil unik qe kombinon tingujt popullore me ritmet moderne, ai ka arritur te fitoje zemrat e mijera degjuesve ne te gjithe boten shqiptare.",
      bio2: "Me hite si \"Aman\" qe ka kaluar 1.3 milion degjime ne Spotify, \"Vera\" me mbi 1 milion degjime, dhe \"Kallma cigaren\" qe vazhdon te jete nder kenget me te degjuara, DJ Smily ka vendosur standardin e ri per muziken shqiptare te remiksueme.",
      bio3: "I disponueshem per dasma, evente private, klube nate, dhe festivale. Cdo event behet i paharrueshem me DJ Smily.",
      stats: {
        listeners: "Degjues Mujore",
        plays: "Degjime ne 'Aman'",
        events: "Evente Live",
        singles: "Single te Publikuara",
      },
    },
    music: {
      overline: "Diskografia",
      title: "Hitet me te",
      titleHighlight: "Degjuara",
      spotifyBtn: "SHIKO TE GJITHA NE SPOTIFY",
    },
    gallery: {
      overline: "Galeria",
      title: "Momente nga",
      titleHighlight: "Skena",
    },
    videos: {
      overline: "Video",
      title: "Shiko ne",
      titleHighlight: "YouTube",
      desc: "Performancat e fundit dhe videot zyrtare nga kanali i DJ Smily.",
      subscribe: "ABONOHU NE YOUTUBE",
    },
    booking: {
      overline: "Rezervime",
      title: "Rezervo",
      titleHighlight: "DJ Smily",
      desc: "Dasma, ditelindja, evente private, klube nate apo festivale — DJ Smily eshte gati te beje eventin tuaj te paharrueshem.",
      contact: "Per rezervime dhe informacione te metejshme, na kontaktoni direkt ne Instagram.",
      cta: "DERGO MESAZH NE INSTAGRAM",
      or: "ose na gjeni ne",
      card1: { title: "Dasma & Festa", desc: "Bejme dasmat tuaja te paharrueshme" },
      card2: { title: "Klube Nate", desc: "Nete te paharrueshme me muzike live" },
      card3: { title: "Festivale", desc: "Disponueshem per festivale nderkombetare" },
    },
    footer: { rights: "Te gjitha te drejtat te rezervuara." },
  },
  en: {
    seo: {
      title: "DJ Smily | Albanian Music Producer & DJ - Weddings, Events & Festivals",
      description: "DJ Smily - Albanian music producer and DJ. Creator of hits 'Aman', 'Vera' and 'Kallma cigaren'. Book for weddings, private events, nightclubs and festivals.",
      keywords: "DJ Smily, Albanian DJ, Albanian music, Albanian remix, wedding DJ, Aman, Vera, Kallma cigaren, music producer Kosovo, DJ Tirana, DJ Pristina",
    },
    nav: { home: "Home", about: "About", music: "Music", videos: "Videos", gallery: "Gallery", booking: "Booking", bookNow: "BOOK NOW" },
    hero: {
      overline: "Albanian Music Producer & DJ",
      subtitle: "Music that makes you feel alive. From folk hits to the best Albanian remixes.",
      cta: "BOOK NOW",
      listen: "LISTEN NOW",
    },
    about: {
      overline: "About Me",
      title: "Passion for",
      titleHighlight: "Music",
      bio1: "DJ Smily is one of the most beloved DJs on the Albanian music scene. With a unique style that combines folk sounds with modern rhythms, he has won the hearts of thousands of listeners across the Albanian world.",
      bio2: "With hits like \"Aman\" which has surpassed 1.3 million streams on Spotify, \"Vera\" with over 1 million streams, and \"Kallma cigaren\" which continues to be among the most listened tracks, DJ Smily has set a new standard for Albanian remix music.",
      bio3: "Available for weddings, private events, nightclubs, and festivals. Every event becomes unforgettable with DJ Smily.",
      stats: {
        listeners: "Monthly Listeners",
        plays: "Plays on 'Aman'",
        events: "Live Events",
        singles: "Published Singles",
      },
    },
    music: {
      overline: "Discography",
      title: "Most",
      titleHighlight: "Listened",
      spotifyBtn: "VIEW ALL ON SPOTIFY",
    },
    gallery: {
      overline: "Gallery",
      title: "Moments from",
      titleHighlight: "The Stage",
    },
    videos: {
      overline: "Videos",
      title: "Watch on",
      titleHighlight: "YouTube",
      desc: "Latest performances and official videos from the DJ Smily channel.",
      subscribe: "SUBSCRIBE ON YOUTUBE",
    },
    booking: {
      overline: "Booking",
      title: "Book",
      titleHighlight: "DJ Smily",
      desc: "Weddings, birthdays, private events, nightclubs or festivals — DJ Smily is ready to make your event unforgettable.",
      contact: "For bookings and more information, contact us directly on Instagram.",
      cta: "SEND MESSAGE ON INSTAGRAM",
      or: "or find us at",
      card1: { title: "Weddings & Parties", desc: "Making your celebrations unforgettable" },
      card2: { title: "Nightclubs", desc: "Unforgettable nights with live music" },
      card3: { title: "Festivals", desc: "Available for international festivals" },
    },
    footer: { rights: "All rights reserved." },
  },
  de: {
    seo: {
      title: "DJ Smily | Albanischer Musikproduzent & DJ - Hochzeiten, Events & Festivals",
      description: "DJ Smily - Albanischer Musikproduzent und DJ. Schopfer der Hits 'Aman', 'Vera' und 'Kallma cigaren'. Buchen Sie fur Hochzeiten, private Veranstaltungen, Nachtclubs und Festivals.",
      keywords: "DJ Smily, Albanischer DJ, Albanische Musik, Albanischer Remix, Hochzeits-DJ, Aman, Vera, Kallma cigaren, Musikproduzent Kosovo, DJ Tirana, DJ Pristina",
    },
    nav: { home: "Startseite", about: "Uber Mich", music: "Musik", videos: "Videos", gallery: "Galerie", booking: "Buchung", bookNow: "JETZT BUCHEN" },
    hero: {
      overline: "Albanischer Musikproduzent & DJ",
      subtitle: "Musik, die dich lebendig fuhlen lasst. Von Volkshits bis zu den besten albanischen Remixes.",
      cta: "JETZT BUCHEN",
      listen: "JETZT ANHOREN",
    },
    about: {
      overline: "Uber Mich",
      title: "Leidenschaft fur",
      titleHighlight: "Musik",
      bio1: "DJ Smily ist einer der beliebtesten DJs der albanischen Musikszene. Mit einem einzigartigen Stil, der Volksklange mit modernen Rhythmen verbindet, hat er die Herzen tausender Zuhorer in der ganzen albanischen Welt erobert.",
      bio2: "Mit Hits wie \"Aman\", der uber 1,3 Millionen Streams auf Spotify erreicht hat, \"Vera\" mit uber 1 Million Streams und \"Kallma cigaren\", der weiterhin zu den meistgehorten Tracks gehort, hat DJ Smily einen neuen Standard fur albanische Remix-Musik gesetzt.",
      bio3: "Verfugbar fur Hochzeiten, private Veranstaltungen, Nachtclubs und Festivals. Jedes Event wird unvergesslich mit DJ Smily.",
      stats: {
        listeners: "Monatliche Zuhorer",
        plays: "Streams bei 'Aman'",
        events: "Live Events",
        singles: "Veroffentlichte Singles",
      },
    },
    music: {
      overline: "Diskographie",
      title: "Die meistgehorten",
      titleHighlight: "Hits",
      spotifyBtn: "ALLE AUF SPOTIFY ANSEHEN",
    },
    gallery: {
      overline: "Galerie",
      title: "Momente von der",
      titleHighlight: "Buhne",
    },
    videos: {
      overline: "Videos",
      title: "Anschauen auf",
      titleHighlight: "YouTube",
      desc: "Die neuesten Auftritte und offiziellen Videos vom DJ Smily Kanal.",
      subscribe: "AUF YOUTUBE ABONNIEREN",
    },
    booking: {
      overline: "Buchung",
      title: "Buchen Sie",
      titleHighlight: "DJ Smily",
      desc: "Hochzeiten, Geburtstage, private Veranstaltungen, Nachtclubs oder Festivals — DJ Smily ist bereit, Ihr Event unvergesslich zu machen.",
      contact: "Fur Buchungen und weitere Informationen kontaktieren Sie uns direkt auf Instagram.",
      cta: "NACHRICHT AUF INSTAGRAM SENDEN",
      or: "oder finden Sie uns unter",
      card1: { title: "Hochzeiten & Feiern", desc: "Wir machen Ihre Feiern unvergesslich" },
      card2: { title: "Nachtclubs", desc: "Unvergessliche Nachte mit Live-Musik" },
      card3: { title: "Festivals", desc: "Verfugbar fur internationale Festivals" },
    },
    footer: { rights: "Alle Rechte vorbehalten." },
  },
};

const LANG_LABELS = { sq: "Shqip", en: "English", de: "Deutsch" };

// Language Context
const LangContext = createContext({ lang: "sq", setLang: () => {}, t: translations.sq });

const useLang = () => useContext(LangContext);

// Social links
const SOCIAL_LINKS = {
  spotify: "https://open.spotify.com/artist/4n5wZTinx2fpLz25Wl9G0Y",
  youtube: "https://www.youtube.com/@Djsmily01",
  instagram: "https://instagram.com/djsmily_official",
  tiktok: "https://vm.tiktok.com/ZMLs92Qg8/",
};

// DJ Smily Original Photos
const DJ_PHOTOS = {
  portrait: "https://customer-assets.emergentagent.com/job_djsmily-official/artifacts/tzz44wyn_ab67616100005174f80c94f4e13e24d40dffe2e4.jpg",
  profile2: "https://customer-assets.emergentagent.com/job_djsmily-official/artifacts/x5c9bgee_ab67616d0000b273f9209a127d0972ea2b1c36a3.jpg",
  livLounge: "https://customer-assets.emergentagent.com/job_djsmily-official/artifacts/1z5evnj8_ab67616d0000b273296726574f9dcb9865876c94.jpg",
};

// Images
const IMAGES = {
  hero: "https://images.pexels.com/photos/3122799/pexels-photo-3122799.jpeg?auto=compress&cs=tinysrgb&w=1920",
  gallery: [
    DJ_PHOTOS.portrait,
    DJ_PHOTOS.profile2,
    DJ_PHOTOS.livLounge,
    "https://images.pexels.com/photos/1190298/pexels-photo-1190298.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/754855/pexels-photo-754855.jpeg?auto=compress&cs=tinysrgb&w=800",
    "https://images.pexels.com/photos/342520/pexels-photo-342520.jpeg?auto=compress&cs=tinysrgb&w=800",
  ],
};

// Spotify Track IDs for embeds
const TRACKS = [
  { id: "09BwhJs1gKNRY2AbPIEPmo", title: "Aman", plays: "1.3M+" },
  { id: "77clJ0A29t2FzGhCOubB99", title: "Vera", plays: "1M+" },
  { id: "3pyIuKJrYMaGF77CnMAhIw", title: "Kallma cigaren", plays: "651K+" },
  { id: "0KdgVLl6GwsBHq3AxojTzw", title: "Dallandyshe - Remix", plays: "22K+" },
];

// YouTube Video IDs for embeds
const VIDEOS = [
  { id: "qcDODMcHg3A", title: "DJ Smily" },
  { id: "Z_QOLjVd0VA", title: "DJ Smily" },
  { id: "pNY3VtImv1U", title: "DJ Smily" },
];

// TikTok SVG Icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.88a8.28 8.28 0 003.76.97V6.4a4.85 4.85 0 01-.01.29z"/>
  </svg>
);

// Spotify SVG Icon
const SpotifyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

// Reveal on scroll hook
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const elements = ref.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));
    return () => elements?.forEach((el) => observer.unobserve(el));
  }, []);
  return ref;
}

// ===== LANGUAGE SWITCHER =====
const LanguageSwitcher = () => {
  const { lang, setLang } = useLang();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="language-switcher" className="flex items-center gap-1.5 text-[#A3A3A3] hover:text-[#D4AF37] transition-colors duration-300 bg-transparent border-none cursor-pointer text-sm uppercase tracking-wider">
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{LANG_LABELS[lang]}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#121212] border-[#2A2A2A] min-w-[140px]">
        {Object.entries(LANG_LABELS).map(([key, label]) => (
          <DropdownMenuItem
            key={key}
            data-testid={`lang-${key}`}
            onClick={() => setLang(key)}
            className={`cursor-pointer text-sm ${lang === key ? "text-[#D4AF37]" : "text-[#A3A3A3]"} hover:text-[#D4AF37] focus:text-[#D4AF37] focus:bg-[#1A1A1A]`}
          >
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ===== NAVIGATION =====
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: t.nav.home, href: "#hero" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.music, href: "#music" },
    { label: t.nav.videos, href: "#videos" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.booking, href: "#booking" },
  ];

  const scrollTo = (href) => {
    setMenuOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav data-testid="main-navigation" className={`glass-nav fixed top-0 left-0 right-0 z-50 ${scrolled ? "scrolled" : ""}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#hero" onClick={() => scrollTo("#hero")} data-testid="nav-logo" className="flex items-center gap-2">
          <Headphones className="w-6 h-6 text-[#D4AF37]" />
          <span className="font-['Playfair_Display'] text-xl font-bold tracking-tight text-white">
            DJ <span className="text-[#D4AF37]">SMILY</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <button key={item.href} onClick={() => scrollTo(item.href)} data-testid={`nav-${item.href.slice(1)}`}
              className="nav-link text-sm font-medium uppercase tracking-[0.15em] text-[#A3A3A3] bg-transparent border-none cursor-pointer">
              {item.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <a href="https://ig.me/m/djsmily_official" target="_blank" rel="noopener noreferrer" data-testid="nav-book-button"
            className="hidden md:inline-block btn-gold text-xs">
            {t.nav.bookNow}
          </a>
          <button data-testid="mobile-menu-toggle" onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden text-white bg-transparent border-none cursor-pointer p-1">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="mobile-menu md:hidden absolute top-20 left-0 right-0 border-t border-white/5 py-6 px-6">
          {navItems.map((item, i) => (
            <button key={item.href} onClick={() => scrollTo(item.href)}
              className="block w-full text-left py-3 text-[#A3A3A3] hover:text-[#D4AF37] text-sm uppercase tracking-[0.15em] bg-transparent border-none cursor-pointer transition-colors duration-300"
              style={{ animationDelay: `${i * 0.05}s` }}>
              {item.label}
            </button>
          ))}
          <a href="https://ig.me/m/djsmily_official" target="_blank" rel="noopener noreferrer"
            className="block mt-4 btn-gold text-center text-xs">
            {t.nav.bookNow}
          </a>
        </div>
      )}
    </nav>
  );
};

// ===== HERO =====
const HeroSection = () => {
  const { t } = useLang();
  return (
    <section id="hero" data-testid="hero-section" className="hero-bg min-h-screen flex items-center justify-center relative"
      style={{ backgroundImage: `url(${IMAGES.hero})` }}>
      <div className="hero-content text-center px-6 max-w-4xl">
        <p className="text-[#D4AF37] text-sm uppercase tracking-[0.25em] font-medium mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}>
          {t.hero.overline}
        </p>
        <h1 className="text-6xl sm:text-7xl lg:text-9xl font-black tracking-tighter leading-none mb-8 animate-fade-in-up opacity-0" style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}>
          DJ <span className="text-[#D4AF37]">SMILY</span>
        </h1>
        <p className="text-[#A3A3A3] text-base md:text-lg max-w-xl mx-auto mb-10 animate-fade-in-up opacity-0" style={{ animationDelay: "0.6s", animationFillMode: "forwards" }}>
          {t.hero.subtitle}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: "0.8s", animationFillMode: "forwards" }}>
          <a href="#booking" onClick={(e) => { e.preventDefault(); document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" }); }}
            data-testid="hero-book-button" className="btn-gold animate-pulse-glow">
            {t.hero.cta}
          </a>
          <a href="#music" onClick={(e) => { e.preventDefault(); document.querySelector("#music")?.scrollIntoView({ behavior: "smooth" }); }}
            data-testid="hero-listen-button" className="btn-gold-outline">
            {t.hero.listen}
          </a>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-6 h-6 text-[#D4AF37] opacity-60" />
        </div>
      </div>
    </section>
  );
};

// ===== ABOUT =====
const AboutSection = () => {
  const sectionRef = useReveal();
  const { t } = useLang();

  const stats = [
    { number: "31K+", label: t.about.stats.listeners, icon: <Users className="w-5 h-5" /> },
    { number: "1.3M+", label: t.about.stats.plays, icon: <Play className="w-5 h-5" /> },
    { number: "100+", label: t.about.stats.events, icon: <CalendarDays className="w-5 h-5" /> },
    { number: "4+", label: t.about.stats.singles, icon: <Music className="w-5 h-5" /> },
  ];

  return (
    <section id="about" data-testid="about-section" ref={sectionRef} className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="reveal mb-16">
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold mb-3">{t.about.overline}</p>
          <div className="gold-line mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            {t.about.title} <span className="text-[#D4AF37]">{t.about.titleHighlight}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="reveal lg:col-span-5 gallery-item rounded-sm overflow-hidden h-[400px] lg:h-[520px]">
            <img src={DJ_PHOTOS.portrait} alt="DJ Smily" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>

          <div className="lg:col-span-7 flex flex-col gap-8">
            <div className="reveal bg-[#121212] border border-[#2A2A2A] rounded-sm p-8 lg:p-10">
              <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed mb-6">{t.about.bio1}</p>
              <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed mb-6">{t.about.bio2}</p>
              <p className="text-[#A3A3A3] text-base md:text-lg leading-relaxed">{t.about.bio3}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="reveal bg-[#121212] border border-[#2A2A2A] rounded-sm p-6 card-hover"
                  style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="text-[#D4AF37] mb-3">{stat.icon}</div>
                  <p className="stat-number text-3xl md:text-4xl mb-1">{stat.number}</p>
                  <p className="text-[#A3A3A3] text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== MUSIC =====
const MusicSection = () => {
  const sectionRef = useReveal();
  const { t } = useLang();

  return (
    <section id="music" data-testid="music-section" ref={sectionRef} className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className="reveal text-center mb-16">
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold mb-3">{t.music.overline}</p>
          <div className="gold-line mx-auto mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            {t.music.title} <span className="text-[#D4AF37]">{t.music.titleHighlight}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {TRACKS.map((track, i) => (
            <div key={track.id} className="reveal spotify-embed" style={{ transitionDelay: `${i * 0.1}s` }}
              data-testid={`spotify-track-${track.title.toLowerCase().replace(/\s/g, '-')}`}>
              <iframe src={`https://open.spotify.com/embed/track/${track.id}?utm_source=generator&theme=0`}
                width="100%" height="152" frameBorder="0" allowFullScreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy" title={track.title} style={{ borderRadius: "12px" }}></iframe>
            </div>
          ))}
        </div>

        <div className="reveal text-center mt-12">
          <a href={SOCIAL_LINKS.spotify} target="_blank" rel="noopener noreferrer" data-testid="spotify-full-link"
            className="btn-gold-outline inline-flex items-center gap-2">
            <SpotifyIcon className="w-4 h-4" />
            {t.music.spotifyBtn}
          </a>
        </div>
      </div>
    </section>
  );
};

// ===== VIDEOS (YouTube) =====
const VideosSection = () => {
  const sectionRef = useReveal();
  const { t } = useLang();

  return (
    <section id="videos" data-testid="videos-section" ref={sectionRef} className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="reveal text-center mb-16">
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold mb-3">{t.videos.overline}</p>
          <div className="gold-line mx-auto mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
            {t.videos.title} <span className="text-[#D4AF37]">{t.videos.titleHighlight}</span>
          </h2>
          <p className="text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto">{t.videos.desc}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {VIDEOS.map((video, i) => (
            <div
              key={`${video.id}-${i}`}
              className="reveal gallery-item rounded-sm overflow-hidden bg-[#121212] border border-[#2A2A2A]"
              style={{ transitionDelay: `${i * 0.1}s` }}
              data-testid={`youtube-video-${i}`}
            >
              <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              <div className="p-4">
                <p className="text-white text-sm font-semibold truncate">{video.title}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="reveal text-center mt-12">
          <a
            href={SOCIAL_LINKS.youtube}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-subscribe-link"
            className="btn-gold-outline inline-flex items-center gap-2"
          >
            <Youtube className="w-4 h-4" />
            {t.videos.subscribe}
          </a>
        </div>
      </div>
    </section>
  );
};

// ===== GALLERY =====
const GallerySection = () => {
  const sectionRef = useReveal();
  const { t } = useLang();

  return (
    <section id="gallery" data-testid="gallery-section" ref={sectionRef} className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="reveal mb-16">
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold mb-3">{t.gallery.overline}</p>
          <div className="gold-line mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            {t.gallery.title} <span className="text-[#D4AF37]">{t.gallery.titleHighlight}</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          <div className="reveal col-span-2 row-span-2 gallery-item rounded-sm h-[300px] md:h-[500px]" data-testid="gallery-image-1">
            <img src={IMAGES.gallery[0]} alt="DJ Smily" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="reveal gallery-item rounded-sm h-[145px] md:h-[242px]" data-testid="gallery-image-2" style={{ transitionDelay: "0.1s" }}>
            <img src={IMAGES.gallery[1]} alt="DJ Smily portrait" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="reveal gallery-item rounded-sm h-[145px] md:h-[242px]" data-testid="gallery-image-3" style={{ transitionDelay: "0.2s" }}>
            <img src={IMAGES.gallery[2]} alt="DJ Smily at LIV Lounge" className="w-full h-full object-cover object-top" loading="lazy" />
          </div>
          <div className="reveal gallery-item rounded-sm h-[200px] md:h-[280px]" data-testid="gallery-image-4" style={{ transitionDelay: "0.3s" }}>
            <img src={IMAGES.gallery[3]} alt="Event crowd dancing" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="reveal gallery-item rounded-sm h-[200px] md:h-[280px]" data-testid="gallery-image-5" style={{ transitionDelay: "0.4s" }}>
            <img src={IMAGES.gallery[4]} alt="Mixing console" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="reveal gallery-item rounded-sm h-[200px] md:h-[280px]" data-testid="gallery-image-6" style={{ transitionDelay: "0.5s" }}>
            <img src={IMAGES.gallery[5]} alt="Nightclub atmosphere" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== BOOKING =====
const BookingSection = () => {
  const sectionRef = useReveal();
  const { t } = useLang();

  return (
    <section id="booking" data-testid="booking-section" ref={sectionRef} className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] relative">
      <div className="max-w-4xl mx-auto text-center booking-glow">
        <div className="reveal">
          <p className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold mb-3">{t.booking.overline}</p>
          <div className="gold-line mx-auto mb-6"></div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            {t.booking.title} <span className="text-[#D4AF37]">{t.booking.titleHighlight}</span>
          </h2>
          <p className="text-[#A3A3A3] text-base md:text-lg max-w-2xl mx-auto mb-4">{t.booking.desc}</p>
          <p className="text-[#A3A3A3] text-base max-w-2xl mx-auto mb-12">{t.booking.contact}</p>
        </div>

        <div className="reveal flex flex-col items-center gap-6">
          <a href="https://ig.me/m/djsmily_official" target="_blank" rel="noopener noreferrer"
            data-testid="booking-instagram-button" className="btn-gold inline-flex items-center gap-3 text-sm animate-pulse-glow">
            <Instagram className="w-5 h-5" />
            {t.booking.cta}
          </a>
          <p className="text-[#A3A3A3] text-sm">
            {t.booking.or} <a href="https://instagram.com/djsmily_official" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">@djsmily_official</a>
          </p>
        </div>

        <div className="reveal grid grid-cols-1 sm:grid-cols-3 gap-4 mt-16">
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6 card-hover">
            <CalendarDays className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            <h4 className="font-['Outfit'] font-semibold text-white text-sm uppercase tracking-wider mb-2">{t.booking.card1.title}</h4>
            <p className="text-[#A3A3A3] text-sm">{t.booking.card1.desc}</p>
          </div>
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6 card-hover">
            <Music className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            <h4 className="font-['Outfit'] font-semibold text-white text-sm uppercase tracking-wider mb-2">{t.booking.card2.title}</h4>
            <p className="text-[#A3A3A3] text-sm">{t.booking.card2.desc}</p>
          </div>
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-sm p-6 card-hover">
            <MapPin className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            <h4 className="font-['Outfit'] font-semibold text-white text-sm uppercase tracking-wider mb-2">{t.booking.card3.title}</h4>
            <p className="text-[#A3A3A3] text-sm">{t.booking.card3.desc}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== FOOTER =====
const Footer = () => {
  const { t } = useLang();
  const socialLinks = [
    { icon: <SpotifyIcon className="w-5 h-5" />, href: SOCIAL_LINKS.spotify, label: "Spotify", testid: "social-spotify-link" },
    { icon: <Youtube className="w-5 h-5" />, href: SOCIAL_LINKS.youtube, label: "YouTube", testid: "social-youtube-link" },
    { icon: <Instagram className="w-5 h-5" />, href: SOCIAL_LINKS.instagram, label: "Instagram", testid: "social-instagram-link" },
    { icon: <TikTokIcon className="w-5 h-5" />, href: SOCIAL_LINKS.tiktok, label: "TikTok", testid: "social-tiktok-link" },
  ];

  return (
    <footer data-testid="footer-section" className="py-16 px-6 md:px-12 lg:px-24 border-t border-[#2A2A2A]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-5xl sm:text-7xl md:text-8xl font-black tracking-tighter opacity-10">DJ SMILY</h2>
        </div>
        <div className="flex items-center justify-center gap-8 mb-10">
          {socialLinks.map((link) => (
            <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer"
              data-testid={link.testid} className="social-icon" aria-label={link.label}>
              {link.icon}
            </a>
          ))}
        </div>
        <div className="text-center">
          <p className="text-[#A3A3A3] text-sm">&copy; {new Date().getFullYear()} DJ Smily. {t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};

// ===== HOME =====
const Home = () => (
  <div className="grain-overlay">
    <Navigation />
    <HeroSection />
    <AboutSection />
    <MusicSection />
    <VideosSection />
    <GallerySection />
    <BookingSection />
    <Footer />
  </div>
);

// ===== SEO META MANAGER =====
const SeoManager = ({ lang, seo }) => {
  useEffect(() => {
    if (!seo) return;
    // Update html lang
    document.documentElement.lang = lang;
    // Title
    document.title = seo.title;

    // Helper to set/update meta tag
    const setMeta = (selector, attr, key, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta('meta[name="description"]', "name", "description", seo.description);
    setMeta('meta[name="keywords"]', "name", "keywords", seo.keywords);

    // Open Graph
    setMeta('meta[property="og:title"]', "property", "og:title", seo.title);
    setMeta('meta[property="og:description"]', "property", "og:description", seo.description);
    setMeta('meta[property="og:type"]', "property", "og:type", "website");
    setMeta('meta[property="og:locale"]', "property", "og:locale", lang === "sq" ? "sq_AL" : lang === "de" ? "de_DE" : "en_US");
    setMeta('meta[property="og:image"]', "property", "og:image", DJ_PHOTOS.portrait);
    setMeta('meta[property="og:url"]', "property", "og:url", typeof window !== "undefined" ? window.location.href : "");

    // Twitter
    setMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", seo.title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", seo.description);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", DJ_PHOTOS.portrait);
  }, [lang, seo]);

  return null;
};

// ===== APP =====
function App() {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("djsmily_lang");
    return saved && translations[saved] ? saved : "sq";
  });

  useEffect(() => {
    localStorage.setItem("djsmily_lang", lang);
  }, [lang]);

  const t = translations[lang];

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      <SeoManager lang={lang} seo={t.seo} />
      <HashRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </HashRouter>
    </LangContext.Provider>
  );
}

export default App;
